import type { JsonSchema, JsonSchemaProperty } from "./schema";

/**
 * `Object.hasOwn` needs `lib` ES2022. This file is copied into apps as part of
 * the bridge carrier, and some of them compile against ES2020.
 */
function hasOwn(value: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

/**
 * Argument validation against the declared `inputSchema`.
 *
 * `docs/error-taxonomy.md` step 2: "a malformed call never reaches the user's tab". This is that
 * check, and it is written here rather than pulled from `ajv` for the reason
 * the whole `manifest/` layer exists — the same code has to run in a Worker, a
 * Durable Object and a browser frame, and the schema subset it must cover is
 * the one `tool.ts` declares and nothing more.
 *
 * `invalid_arguments` must name THE SCHEMA PATH THAT FAILED (`docs/error-taxonomy.md`). A validator
 * that answers only true/false forces the agent to guess which argument was
 * wrong, which for a five-property schema is five more round trips.
 */
export type ValidationResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly path: string; readonly message: string };

function bad(path: string, message: string): ValidationResult {
  return { ok: false, path, message };
}

export function validateArguments(schema: JsonSchema, args: unknown): ValidationResult {
  if (typeof args !== "object" || args === null || Array.isArray(args)) {
    return bad("arguments", "arguments must be a JSON object");
  }
  const value = args as Record<string, unknown>;
  const properties = schema.properties ?? {};

  for (const name of schema.required ?? []) {
    if (!hasOwn(value, name)) {
      return bad(`arguments.${name}`, `required property "${name}" is missing`);
    }
  }

  if (schema.additionalProperties === false) {
    for (const name of Object.keys(value)) {
      if (!hasOwn(properties, name)) {
        return bad(
          `arguments.${name}`,
          `unknown property "${name}" — this schema declares ${describeKeys(properties)}`,
        );
      }
    }
  }

  for (const [name, property] of Object.entries(properties)) {
    if (!hasOwn(value, name)) continue;
    const problem = checkProperty(`arguments.${name}`, property, value[name]);
    if (problem !== null) return problem;
  }

  return { ok: true };
}

function checkProperty(
  path: string,
  property: JsonSchemaProperty,
  value: unknown,
): ValidationResult | null {
  const actual = typeOf(value);
  if (actual !== property.type) {
    return bad(path, `expected ${property.type}, received ${actual}`);
  }
  if (property.enum !== undefined && typeof value === "string" && !property.enum.includes(value)) {
    return bad(path, `"${value}" is not one of ${property.enum.join(", ")}`);
  }
  if (typeof value === "number") {
    if (property.minimum !== undefined && value < property.minimum) {
      return bad(path, `${value} is below the minimum of ${property.minimum}`);
    }
    if (property.maximum !== undefined && value > property.maximum) {
      return bad(path, `${value} is above the maximum of ${property.maximum}`);
    }
    // NaN and ±Infinity pass `typeof value === "number"` and then become `null`
    // in JSON — the same silent shape change `safety/` rejects on the way out.
    if (!Number.isFinite(value)) return bad(path, `${String(value)} is not a finite number`);
  }
  return null;
}

function typeOf(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
}

function describeKeys(properties: Readonly<Record<string, JsonSchemaProperty>>): string {
  const names = Object.keys(properties);
  return names.length === 0 ? "no properties" : names.join(", ");
}
